import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, PanelRightOpen } from 'lucide-react';

const STEP_VARIANT: Record<string, 'default' | 'secondary' | 'destructive' | 'outline' | 'pending' | 'confirmed' | 'inProgress' | 'completed' | 'cancelled'> = {
  IDLE: 'outline',
  NEW_INQUIRY: 'pending',
  SELECTING_SERVICE: 'inProgress',
  SELECTING_DATE: 'inProgress',
  SELECTING_TIME: 'inProgress',
  CONFIRMING: 'secondary',
  BOOKING_IN_PROGRESS: 'inProgress',
  AWAITING_PAYMENT: 'pending',
  PAYMENT_SENT: 'secondary',
  PAYMENT_OVERDUE: 'destructive',
  PAYING: 'pending',
  PAID: 'confirmed',
  CONFIRMED: 'confirmed',
  APPOINTMENT_DAY: 'inProgress',
  COMPLETED: 'completed',
  NO_SHOW: 'destructive',
  CANCELLED: 'cancelled',
};

interface ChatHeaderProps {
  customerName: string | null;
  customerPhone: string;
  currentStep: string;
  assignedOperatorName?: string | null;
  onBack: () => void;
  onInfoToggle: () => void;
}

export function ChatHeader({
  customerName,
  customerPhone,
  currentStep,
  assignedOperatorName,
  onBack,
  onInfoToggle,
}: ChatHeaderProps) {
  const { t } = useTranslation();

  const initials = customerName
    ? customerName.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()
    : '?';

  const displayName = customerName || customerPhone;
  const stepLabel = t(`conversations.steps.${currentStep}`, { defaultValue: currentStep.replace(/_/g, ' ') });
  const variant = STEP_VARIANT[currentStep] || 'outline';

  return (
    <div className="flex items-center gap-3 px-4 py-2.5 border-b bg-background shrink-0">
      <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={onBack}>
        <ArrowLeft className="h-4 w-4" />
      </Button>

      <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center text-xs font-semibold text-primary shrink-0">
        {initials}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-semibold text-sm truncate">{displayName}</span>
          <Badge variant={variant} className="gap-1 px-2 py-0.5 text-[11px] shrink-0">
            {stepLabel}
          </Badge>
        </div>
        {assignedOperatorName && (
          <p className="text-xs text-muted-foreground truncate">
            {t('conversations.assignment.assignedTo')} {assignedOperatorName}
          </p>
        )}
      </div>

      <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={onInfoToggle}>
        <PanelRightOpen className="h-4 w-4" />
      </Button>
    </div>
  );
}
